/* Import components */

import React, {useEffect, useState, useContext, useMemo, useCallback} from 'react';
import {useSelector} from 'react-redux';
import {Link, useHistory} from 'react-router-dom';
import {axios} from '../libs';
import {User} from '../states/actions';
import { GlobalStateContext } from '../components/GlobalState';
import { InlineSvgIcon } from './InlineSvgIcon';
import { useTranslation } from '../i18n/I18nContext';
import logger from '../utils/logger';

/* Export sidebar */

const SidebarComponent = () => {
	const { t } = useTranslation();
	const { globalState, setGlobalState } = useContext(GlobalStateContext);
	const user                            = useSelector(User.get());
	const {replace}                       = useHistory();
	
	// Memoize user data to prevent unnecessary re-renders
	const memoizedUser = useMemo(() => {
		logger.log('🔍 Sidebar component - User:', { role: user.role, user_no: user.user_no });
		return user;
	}, [user.role, user.user_no, user.fullName, user.avatar, user.is_active_for_matching, user.lawyer_level]);

	const updateLawyerProfileData = useCallback(async (userId) => {
		if (!userId) return;
		try {
			await axios.post('/api/update-lawyer-profile', { user_id: userId });
		} catch (error) {
			logger.error('Error auto-updating lawyer profile data:', error);
		}
	}, []);

	const toggleMatchmakingActive = useCallback(async () => {
		if (memoizedUser?.role !== 'lawyer') return;

		if (memoizedUser.lawyer_level === 'basic') {
			window.nError({
				title: t('profile.notAvailable'),
				text: t('profile.basicModeUpgrade'),
				icon: 'fa-exclamation-circle',
			});
			return;
		}

		const newValue = !memoizedUser.is_active_for_matching;

		try {
			await axios.put(`/user/${memoizedUser._id}`, {
				firstName: memoizedUser.firstName,
				lastName: memoizedUser.lastName,
				email: memoizedUser.email,
				role: memoizedUser.role,
				is_active_for_matching: newValue,
			});

			User.set({ is_active_for_matching: newValue });
			await updateLawyerProfileData(memoizedUser._id);

			window.nSuccess({
				title: t('profile.successTitle'),
				text: newValue ? t('profile.matchmakingActivated') : t('profile.matchmakingDeactivated'),
				icon: 'fa-check-square',
			});
		} catch (error) {
			window.nError({
				title: t('profile.errorTitle'),
				text: error.message || t('profile.matchmakingUpdateFailed'),
				icon: 'fa-exclamation-triangle',
			});
		}
	}, [memoizedUser, t, updateLawyerProfileData]);
	
	useEffect(() => {window.initSidebar()}, []);
	
	// Fetch inquiry count
	useEffect(() => {
		const fetchInquiryCount = async () => {
			try {
				const response = await axios.get('/projects', {
					params: {
						show_inquiries_only: true,
						page: 1,
						sizePerPage: 1 // We only need the total count
					}
				});
				if (response && response.total !== undefined) {
					setInquiryCount(response.total);
				}
			} catch (error) {
				logger.error('Error fetching inquiry count:', error);
			}
		};
		
		fetchInquiryCount();
		// Refresh count every 60 seconds
		const interval = setInterval(fetchInquiryCount, 60000);
		return () => clearInterval(interval);
	}, []);
	
	logger.log(window)
	
	/* Menu handlers */
	
	const [genmenu, setGenmenu] = useState(true);
	const [appmenu, setAppmenu] = useState(false);
	const [settingsmenu, setSettingsmenu] = useState(false);
	const [settingsmenuOpen, setSettingsmenuOpen] = useState(false);
	const [inquiryCount, setInquiryCount] = useState(0);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	
	const logout = useCallback(async () => {
		axios.clearJWT();
		User.clear();
		try {
			await axios.get('/logout', {}, {silent: true});
		} catch (_) {
			/* CRM has no GET /logout route; clearing local session is enough */
		}
		replace('/login');
	}, [replace]);
	
	const setMenu = useCallback(() => {
		// Toggle app menu expansion
		// When expanding apps, collapse main menu
		const newAppmenu = !appmenu;
		setAppmenu(newAppmenu);
		if (newAppmenu) {
			setGenmenu(false); // Collapse main menu when expanding apps
		}
	}, [appmenu]);
	
	const setMenuGen = useCallback(() => {
		// Toggle main menu expansion
		// When expanding main, collapse secondary menus
		const newGenmenu = !genmenu;
		setGenmenu(newGenmenu);
		if (newGenmenu) {
			setAppmenu(false); // Collapse apps when expanding main
			setSettingsmenuOpen(false); // Collapse settings when expanding main
		}
	}, [genmenu]);
	
	const setMenuSettings = useCallback(() => {
		const newSettingsmenu = !settingsmenu;
		setSettingsmenu(newSettingsmenu);
		// When opening settings menu, expand it and collapse main menu (like apps menu)
		if (newSettingsmenu) {
			setSettingsmenuOpen(true);
			setGenmenu(false); // Collapse main menu when opening settings
			setAppmenu(false);
		} else {
			// When closing from main menu, reset expansion state
			setSettingsmenuOpen(false);
		}
	}, [settingsmenu]);
	
	const toggleSettingsMenuOpen = useCallback(() => {
		// Toggle settings menu expansion
		// When expanding settings, collapse main menu
		setSettingsmenuOpen(prev => {
			const newValue = !prev;
			if (newValue) {
				setGenmenu(false); // Collapse main menu when expanding settings
			}
			return newValue;
		});
	}, []);
	
	// Memoized function to set global state for minmenu and calculate sidebar width
	const setMinMenu = useCallback(() => {
		const shouldMinimize = genmenu === false && appmenu === false && settingsmenu === false;
		if (globalState.minmenu !== shouldMinimize) {
			setGlobalState(prev => ({...prev, minmenu: shouldMinimize}));
		}
		document.body.classList.toggle('sidebar-collapsed', shouldMinimize);
		
		// Calculate total sidebar width based on visible and expanded menus
		let totalWidth = 0;
		const hasSubmenu = memoizedUser.role === 'admin' || memoizedUser.role === 'builder';
		// Main menu: lawyer/support expanded = 230px (admin default width), collapsed = 110px
		totalWidth += genmenu ? (hasSubmenu ? 175 : 230) : (hasSubmenu ? 55 : 110);
		// Submenu rail only for roles that have apps/admin submenus
		if (hasSubmenu) {
			if (globalState.appid !== undefined && !settingsmenu) {
				totalWidth += appmenu ? 175 : 55;
			} else if (settingsmenu) {
				totalWidth += settingsmenuOpen ? 175 : 55;
			} else {
				totalWidth += 55;
			}
		}
		
		// Update sidebar width and content area width via inline styles
		const menuContainer = document.querySelector('.menu-container');
		const mainContentArea = document.querySelector('.main_content_area');
		if (menuContainer) {
			menuContainer.style.width = `${totalWidth}px`;
		}
		if (mainContentArea) {
			mainContentArea.style.width = `calc(100% - ${totalWidth}px)`;
		}
	}, [genmenu, appmenu, settingsmenu, settingsmenuOpen, globalState.appid, globalState.minmenu, setGlobalState, memoizedUser.role]);
	
	useEffect(() => {
		setMinMenu();
	}, [setMinMenu]);

	const toggleMobileMenu = useCallback(() => {
		setMobileMenuOpen((prev) => {
			const next = !prev;
			if (next) {
				setGenmenu(true);
			}
			return next;
		});
	}, []);

	const closeMobileMenu = useCallback(() => {
		setMobileMenuOpen(false);
	}, []);

	useEffect(() => {
		document.body.classList.toggle('mobile-menu-open', mobileMenuOpen);
		return () => document.body.classList.remove('mobile-menu-open');
	}, [mobileMenuOpen]);

	useEffect(() => {
		const onResize = () => {
			if (window.innerWidth > 768) {
				setMobileMenuOpen(false);
			}
		};
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	const showNavLabels = genmenu || mobileMenuOpen;
	const showAppLabels = appmenu || mobileMenuOpen;
	const showSettingsLabels = settingsmenuOpen || mobileMenuOpen;

	const hasSubmenuRail = memoizedUser.role === 'admin' || memoizedUser.role === 'builder';
	
	/* Render HTML */
	
	// Check if both main and sub menus are minimized for logo
	const mainMinimized = !genmenu;
	const subMinimized = settingsmenu ? !settingsmenuOpen : (!appmenu || globalState.appid === undefined);
	const bothMinimized = hasSubmenuRail ? (mainMinimized && subMinimized) : mainMinimized;
	
	return (
		<>
			<button
				type="button"
				className="mobile-menu-btn"
				onClick={toggleMobileMenu}
				aria-label={mobileMenuOpen ? t('sidebar.closeMenu') : t('sidebar.openMenu')}
				aria-expanded={mobileMenuOpen}
			>
				<i className={mobileMenuOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'} />
			</button>
			{mobileMenuOpen && (
				<button
					type="button"
					className="mobile-menu-overlay"
					onClick={closeMobileMenu}
					aria-label={t('sidebar.closeMenu')}
				/>
			)}
		<div
			className={`${bothMinimized ? 'sm' : ''} ${!hasSubmenuRail ? 'no-submenu' : ''} ${mobileMenuOpen ? 'mobile-open' : ''} menu-container`}
			onClick={(e) => {
				if (mobileMenuOpen && e.target.closest('a')) {
					closeMobileMenu();
				}
			}}
		>
			<div className="navbar header">
				<Link to="/" className="site_title">
					{bothMinimized ? (
					<span>
						<img
							width={hasSubmenuRail ? 56 : 36}
							height={hasSubmenuRail ? undefined : 36}
							src={hasSubmenuRail ? '/images/logo.png' : '/images/hugo-mark.png'}
							alt={t('sidebar.logoAlt')}
						/>
					</span>
					):(
					<span><img width={106} src="/images/logo_l.png" alt={t('sidebar.logoAlt')} /></span>
					)}
				</Link>
			</div>
			<div id="sidebar-menu" className="main_menu_side hidden-print main_menu">
				<div 
					className={`${genmenu ? 'open' : ''} menu_section general`}
					onClick={(e) => {
						// Toggle if clicking on empty area or menuswitcher, but not on links
						if (!e.target.closest('a') && !e.target.closest('li')) {
							setMenuGen();
						}
					}}
					style={{cursor: 'pointer'}}
				>
					<ul className="nav side-menu">
						{/* Only show Apps for non-lawyer users */}
						{memoizedUser.role !== 'lawyer' && memoizedUser.role !== 'support' && (
						<li>
							<Link 
								to="/chatbots" 
								title={t('sidebar.apps')}
								onClick={() => {
									// If app is active, also open apps submenu and collapse main menu
									if (globalState.appid !== undefined) {
										setAppmenu(true);
										setGenmenu(false);
										setSettingsmenu(false);
									}
									// Always navigate to /chatbots (don't prevent default)
								}}
							>
							<InlineSvgIcon className="svg-icon" src="/svg/icon/apps.svg" aria-label={t('sidebar.apps')} />
							{showNavLabels ? t('sidebar.apps') : ""}
							</Link>
						</li>
						)}
						{(memoizedUser.role === 'admin' || memoizedUser.role === 'support') && (
						<li>
							<Link to="/leads" title={t('sidebar.leads')}>
							<InlineSvgIcon className="svg-icon" src="/svg/icon/info.svg" aria-label={t('sidebar.leads')} />
							{showNavLabels ? t('sidebar.leads') : ""}
							</Link>
						</li>
						)}
					<li>
						<Link to="/projects" title={t('sidebar.projects')}>
						<InlineSvgIcon className="svg-icon" src="/svg/icon/projects.svg" aria-label={t('sidebar.projects')} />
						{showNavLabels ? t('sidebar.projects') : ""}
						</Link>
					</li>
			{memoizedUser.role !== 'lawyer' && memoizedUser.role !== 'support' && memoizedUser.role !== 'builder' && (
			<li>
				<Link to="/inquiries" title={t('sidebar.inquiries')}>
				<InlineSvgIcon className="svg-icon" src="/svg/icon/info.svg" aria-label={t('sidebar.inquiries')} />
				{showNavLabels ? `${t('sidebar.inquiries')}${inquiryCount > 0 ? ` (${inquiryCount})` : ''}` : ""}
				</Link>
			</li>
			)}
					{memoizedUser.role !== 'support' && (
					<li>
						<Link to="/invoices" title={t('sidebar.invoices')}>
						<InlineSvgIcon className="svg-icon" src="/svg/icon/invoices.svg" aria-label={t('sidebar.invoices')} />
						{showNavLabels ? t('sidebar.invoices') : ""}
						</Link>
					</li>
					)}
						{memoizedUser.role !== 'lawyer' && (
						<li>
							<Link to="/clients" title={t('sidebar.clients')}>
							<InlineSvgIcon className="svg-icon" src="/svg/icon/clients.svg" aria-label={t('sidebar.clients')} />
							{showNavLabels ? t('sidebar.clients') : ""}
							</Link>
						</li>
						)}
						{/* Only show Subscriptions for non-lawyer users */}
						{memoizedUser.role !== 'lawyer' && memoizedUser.role !== 'support' && memoizedUser.role !== 'builder' && (
						<li>
							<Link to="/subscriptions" title={t('sidebar.subscriptions')}>
							<InlineSvgIcon className="svg-icon" src="/svg/icon/subscriptions.svg" aria-label={t('sidebar.subscriptions')} />
							{showNavLabels ? t('sidebar.subscriptions') : ""}
							</Link>
						</li>
						)}
					{/* Admin for admins - opens nested menu and navigates to users page */}
					{memoizedUser.role === 'admin' && (
					<li>
						<Link 
							to="/users"
							title={t('sidebar.admin')} 
							onClick={() => {
								setMenuSettings();
							}}
							className="nav-link-style"
							style={{cursor: 'pointer'}}
						>
							<InlineSvgIcon className="svg-icon" src="/svg/icon/settings.svg" aria-label={t('sidebar.admin')} />
							{showNavLabels ? t('sidebar.admin') : ""}
						</Link>
					</li>
					)}
					{memoizedUser.role !== 'support' && (
					<li>
						<Link to="/profile" title={t('sidebar.profile')}>
						<InlineSvgIcon className="svg-icon" src="/svg/icon/admin.svg" aria-label={t('sidebar.profile')} />
						{showNavLabels ? t('sidebar.profile') : ""}
						</Link>
					</li>
					)}
					</ul>
					<span className='menuswitcher' onClick={()=> {setMenuGen()}}>{!genmenu ? <i className="fa-solid fa-angle-right"></i> :<i className="fa-solid fa-angle-left"></i>}</span>
				</div>
				{/* Submenu area - only for roles with apps/admin submenus */}
				{hasSubmenuRail && (
				<div 
					className={`menu_section ${(settingsmenu && settingsmenuOpen) || (globalState.appid !== undefined && !settingsmenu && appmenu) ? 'open' : ''}`}
					onClick={(e) => {
						// Toggle if clicking on empty area or menuswitcher, but not on links
						if (!e.target.closest('a') && !e.target.closest('li')) {
							if (settingsmenu) {
								toggleSettingsMenuOpen();
							} else if (globalState.appid !== undefined) {
								setMenu();
							}
						}
					}}
					style={{cursor: 'pointer'}}
				>
					{settingsmenu ? (
						<>
							<ul className="nav side-menu">
								{memoizedUser.role !== 'lawyer' && memoizedUser.role !== 'support' && (
								<li>
									<Link title={t('sidebar.lawyers')} to="/users">
									<InlineSvgIcon className="svg-icon" src="/svg/icon/admin.svg" aria-label={t('sidebar.lawyers')} />
									{showSettingsLabels ? t('sidebar.lawyers') : ""}
									</Link>
								</li>
								)}
								{memoizedUser.role === 'admin' && (
								<li>
									<Link title={t('sidebar.match')} to="/match">
									<InlineSvgIcon className="svg-icon" src="/svg/icon/projects.svg" aria-label={t('sidebar.match')} />
									{showSettingsLabels ? t('sidebar.match') : ""}
									</Link>
								</li>
								)}
								{memoizedUser.role === 'admin' && (
								<li>
									<Link title={t('sidebar.legalFields')} to="/legal-fields">
									<InlineSvgIcon className="svg-icon" src="/svg/icon/legal-fields.svg" aria-label={t('sidebar.legalFields')} />
									{showSettingsLabels ? t('sidebar.legalFields') : ""}
									</Link>
								</li>
								)}
								{/* Only show Documents for non-lawyer users */}
								{memoizedUser.role !== 'lawyer' && memoizedUser.role !== 'support' && (
								<li>
									<Link title={t('sidebar.documents')} to="/documents">
									<InlineSvgIcon className="svg-icon" src="/svg/icon/documents.svg" aria-label={t('sidebar.documents')} />
									{showSettingsLabels ? t('sidebar.documents') : ""}
									</Link>
								</li>
								)}
								{/* Only show Sheets for non-lawyer users */}
								{memoizedUser.role !== 'lawyer' && memoizedUser.role !== 'support' && (
								<li>
									<Link title={t('sidebar.sheets')} to="/sheets">
									<InlineSvgIcon className="svg-icon" src="/svg/icon/sheets.svg" aria-label={t('sidebar.sheets')} />
									{showSettingsLabels ? t('sidebar.sheets') : ""}
									</Link>
								</li>
								)}
								{memoizedUser.role === 'admin' && (
								<li>
									<Link title={t('sidebar.general')} to="/settings">
									<InlineSvgIcon className="svg-icon" src="/svg/icon/settings.svg" aria-label={t('sidebar.general')} />
									{showSettingsLabels ? t('sidebar.general') : ""}
									</Link>
								</li>
								)}
							</ul>
							<span className='menuswitcher' onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSettingsMenuOpen(); }}>{settingsmenuOpen ? <i className="fas fa-angle-left"></i> : <i className="fas fa-angle-right"></i>}</span>
						</>
					) : globalState.appid !== undefined ? (
						<>
							<ul className="nav side-menu">
								<li>
									<Link title={t('sidebar.builder')} to={`/chatbot-builder/${globalState.appid}`} onClick={logger.log(window.appid)}>
									<InlineSvgIcon className="svg-icon" src="/svg/icon/builder.svg" aria-label={t('sidebar.builder')} />
									{showAppLabels ? t('sidebar.builder') : ""}
									</Link>
								</li>
								<li>
									<Link title={t('sidebar.data')} to={`/chatbot/conversation/${globalState.appid}`} onClick={logger.log(window.appid)}>
									<InlineSvgIcon className="svg-icon" src="/svg/icon/data.svg" aria-label={t('sidebar.data')} />
									{showAppLabels ? t('sidebar.data') : ""}
									</Link>
								</li>
								<li>
									<Link title={t('sidebar.settings')} to={`/chatbots/${globalState.appid}`} onClick={logger.log(window.appid)}>
									<InlineSvgIcon className="svg-icon" src="/svg/icon/settings.svg" aria-label={t('sidebar.settings')} />
									{showAppLabels ? t('sidebar.settings') : ""}
									</Link>
								</li>
							</ul>
							<span className='menuswitcher' onClick={()=> {setMenu()}}>{appmenu ? <i className="fas fa-angle-left"></i> : <i className="fas fa-angle-right"></i>}</span>
						</>
					) : null}
				</div>
				)}
			</div>
			<div className="menu_bottom">
				<ul className="nav side-menu">
					{memoizedUser.role === 'lawyer' && (
					<li>
						<span
							className="menu-bottom-matchmaking"
							title={
								memoizedUser.lawyer_level === 'basic'
									? t('profile.cantChangeBasicMode')
									: t('sidebar.openToNewCases')
							}
						>
							<div className="btn-switch sidebar-open-cases-switch" onClick={toggleMatchmakingActive}>
								<span className="open-cases-label">
									<span
										className={`open-cases-dot ${memoizedUser.is_active_for_matching ? 'on' : 'off'}`}
										aria-hidden="true"
									/>
									{(showNavLabels || showAppLabels || showSettingsLabels) && (
										<span className="text">
											{t('sidebar.openToNewCases')}
										</span>
									)}
								</span>
								<div className={`switch ${memoizedUser.is_active_for_matching ? 'on' : 'off'}${memoizedUser.lawyer_level === 'basic' ? ' disabled' : ''}`}>
									<span></span>
								</div>
							</div>
						</span>
					</li>
					)}
					<li>
						<a
							href="https://docs.google.com/document/d/1DInTfA2LJpZLrt0_JrvoFHl9DAh14VfDOq2dhM_wH_E/edit?usp=sharing"
							target="_blank"
							rel="noopener noreferrer"
						>
							<i className='fa-solid fa-question'></i>
							{showNavLabels ? t('sidebar.help') : ''}
						</a>
					</li>
					<li>
						<span className="" title={t('sidebar.logout')}>
							{(showNavLabels || showAppLabels || showSettingsLabels) &&<>{memoizedUser.fullName}<br/>{memoizedUser.email}</>}
							<i className='fa-solid fa-arrow-right-from-bracket' onClick={logout} ></i>
						</span>
					</li>
				</ul>
			</div>
		</div>
		</>
	);
};

// Memoize the entire sidebar component to prevent unnecessary re-renders
export const Sidebar = React.memo(SidebarComponent);
